import React from 'react';
import PropTypes from 'prop-types';
import { getClickAttrs } from '../Analytics/gtag';
import styles from './popOverMenu.scss';

class PopOverMenu extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isMenuShowed: false,
            displayMenuBelowAnchor: true,
        };
        // Guards the one-shot post-open flip correction (componentDidUpdate). An instance field, not
        // state, so the common no-flip path doesn't trigger an extra render.
        this.flipChecked = false;
        this.handleAchorClick = this.handleAchorClick.bind(this);
        this.setWrapperRef = this.setWrapperRef.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.menuItemClick = this.menuItemClick.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
        document.addEventListener('touchend', this.handleClickOutside, false);
    }

    componentDidUpdate() {
        if (this.state.isMenuShowed) {
            this.scrollSelectedItemIntoView();
            this.correctMenuOpenDirection();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        document.removeEventListener('touchend', this.handleClickOutside, false);
    }

    setWrapperRef(node) {
        this.wrapperRef = node;
    }

    handleAchorClick(ev) {
        ev.preventDefault();
        ev.stopPropagation();
        const { alignLeft, menuItems } = this.props;
        // Open the menu on whichever side has room, so it isn't cut off near the bottom of a short list
        // or the viewport (the old rule only flipped above when the click was in the bottom third).
        const anchorRect = ev.currentTarget?.getBoundingClientRect();
        let showBelow = true;
        if (anchorRect) {
            // The menu list is capped at MENU_MAX_HEIGHT (popOverMenu.scss); estimate its height from the
            // item count so the flip decision is based on the space the menu will actually need.
            const MENU_ITEM_HEIGHT = 36;
            const MENU_MAX_HEIGHT = 500;
            const estimatedMenuHeight = Math.min(MENU_MAX_HEIGHT, (menuItems?.length || 1) * MENU_ITEM_HEIGHT);
            const spaceBelow = window.innerHeight - anchorRect.bottom;
            const spaceAbove = anchorRect.top;
            showBelow = spaceBelow >= estimatedMenuHeight || spaceBelow >= spaceAbove;
        } else if (ev.nativeEvent.clientY > ((2 * window.innerHeight) / 3)) {
            showBelow = false;
        }
        this.flipChecked = false;
        this.setState({
            isMenuShowed: true,
            displayMenuBelowAnchor: showBelow,
            alignLeft,
        });
    }

    menuItemClick(menuItem) {
        return (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            if (this.props.onSelected) {
                this.props.onSelected(menuItem, ev);
            }
            this.setState({
                isMenuShowed: false,
            });
        };
    }

    handleClickOutside(event) {
        if (this.wrapperRef && !this.wrapperRef.contains(event.target) && this.state.isMenuShowed) {
            event.stopPropagation();
            event.preventDefault();
            this.setState({
                isMenuShowed: false,
            });
        }
    }

    scrollSelectedItemIntoView() {
        const { selectedValue, menuItems } = this.props;
        if (!selectedValue) {
            return;
        }
        const menu = menuItems.find(item => item.divisionName === selectedValue || item.value === selectedValue);
        if (!menu) {
            return;
        }
        const list = document.getElementById('list');
        const element = document.getElementById(`item${menu.value}`);
        if (list && element && list.scrollHeight > list.clientHeight) {
            list.scrollTop = element.offsetTop;
        }
    }

    // Correct the open direction using the menu's ACTUAL rendered height: if a below-anchor menu runs
    // past the bottom of its clip boundary, flip it above so it isn't cut off. One-shot per open
    // (this.flipChecked), so it can't loop.
    correctMenuOpenDirection() {
        if (this.flipChecked || !this.wrapperRef) {
            return;
        }
        this.flipChecked = true;
        const menuRect = this.wrapperRef.getBoundingClientRect();
        const overflowsBottom = this.state.displayMenuBelowAnchor && menuRect.bottom > this.resolveClipBoundary();
        if (overflowsBottom) {
            this.setState({ displayMenuBelowAnchor: false });
        }
    }

    // The bottom boundary is the nearest scroll/clip ancestor (e.g. the players table's
    // horizontal-scroll wrapper, whose `overflow:auto` also clips vertically), falling back to the
    // viewport. Using it — not just the viewport — flips short menus that fit the screen but not the
    // scroll box.
    resolveClipBoundary() {
        let boundary = window.innerHeight;
        let el = this.wrapperRef.parentElement;
        while (el && el !== document.body) {
            const { overflowY } = window.getComputedStyle(el);
            if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') {
                boundary = Math.min(boundary, el.getBoundingClientRect().bottom);
                break;
            }
            el = el.parentElement;
        }
        return boundary;
    }

    render() {
        const { selectedValue, 'data-testid': dataTestId } = this.props;
        const List = () => {
            return (
                <div
                    id="list"
                    className={`popovermenu-list ${this.props.popOverMenuClass ? this.props.popOverMenuClass : ''} `}
                    data-testid={dataTestId && `${dataTestId}.list`}
                >
                    {this.props.menuItems && this.props.menuItems.map((menuItem) => {
                        const getMenuItemClassName = (menuItem?.divisionName && selectedValue && selectedValue === menuItem.divisionName) || (selectedValue && menuItem.value === selectedValue) ? 'text-blue' : '';
                        // A menu item may pin its own testid suffix (e.g. the List Detail row's `.menu.note`
                        // whose ticket contract omits the `item-` prefix its sibling `.item-remove` carries);
                        // absent that, keep the default `item-${value}` every other consumer relies on.
                        const itemTestIdSuffix = menuItem && (menuItem.testId || `item-${menuItem.value}`);
                        const itemTestId = dataTestId && menuItem && `${dataTestId}.${itemTestIdSuffix}`;
                        return (
                            (menuItem && (typeof menuItem.show === 'undefined' || (typeof menuItem.show !== 'undefined' && menuItem.show !== false)))
                                ? (
                                    <div
                                        id={`item${menuItem.value}`}
                                        key={menuItem.value}
                                        className="menu-item"
                                        onClick={this.menuItemClick(menuItem)}
                                        data-testid={itemTestId}
                                        {...getClickAttrs({
                                            category: menuItem.clickCategory,
                                            context: menuItem.clickContext,
                                            location: menuItem.clickLocation,
                                        })}
                                    >
                                        {menuItem.playing ? (
                                            <div className={`draw-label`} data-testid={itemTestId && `${itemTestId}.label`}>
                                                {menuItem.label}
                                                <div className={`tag tag-green tag-xs user-status`} data-testid={itemTestId && `${itemTestId}.playing-tag`}>Playing</div>
                                            </div>
                                        ) : (
                                            <div className={getMenuItemClassName} data-testid={itemTestId && `${itemTestId}.label`}>
                                                {menuItem.label}
                                            </div>
                                        )}
                                    </div>
                                )
                                : null
                        );
                    })}
                </div>
            );
        };

        return (
            <div className={`${styles.popOverMenu} ${this.props.popOverMenuClass ? this.props.popOverMenuClass : ''} ${this.props.children ? '' : styles.ellipsis}`} data-testid={dataTestId || undefined}>
                <div
                    className={`popovermenu-anchor ${this.props.anchorClass ? this.props.anchorClass : ''}`}
                    onClick={this.handleAchorClick}
                    data-testid={dataTestId && `${dataTestId}.anchor`}
                >
                    {this.props.children ? this.props.children : <span className={`far ${this.props.horizontalEllipsis ? 'fa-ellipsis-h' : 'fa-ellipsis-v'}`} />}
                </div>
                {this.state.isMenuShowed && (
                    <div className="anchor-click-disabler" />
                )}
                <div
                    className={`menu-container ${this.state.displayMenuBelowAnchor ? 'below-anchor' : 'above-anchor'} ${this.state.alignLeft ? 'align-left' : 'align-right'} ${this.props.mobileAlignLeft} `}
                    ref={this.setWrapperRef}
                >
                    {this.state.isMenuShowed && (
                        <List />
                    )}
                </div>
            </div>

        );
    }
}

PopOverMenu.propTypes = {
    mobileAlignLeft: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
    ]),
    menuItems: PropTypes.arrayOf(PropTypes.any).isRequired,
    selectedValue: PropTypes.string,
    alignLeft: PropTypes.bool,
    onSelected: PropTypes.func.isRequired,
    popOverMenuClass: PropTypes.string,
    anchorClass: PropTypes.string,
    horizontalEllipsis: PropTypes.bool,
    children: PropTypes.node,
    'data-testid': PropTypes.string,
};

PopOverMenu.defaultProps = {
    mobileAlignLeft: false,
    selectedValue: '',
    alignLeft: false,
    popOverMenuClass: '',
    anchorClass: '',
    horizontalEllipsis: false,
    children: null,
    'data-testid': null,
};

export default PopOverMenu;
